import React, { useState, Component } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Coffee, Heart, Sparkles, CheckCircle, MessageSquare, GitCommit, RefreshCw, Image, X } from 'lucide-react';
// Image Modal Component
const ImageModal = ({
  isOpen,
  onClose,
  imageUrl,
  altText
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-id="element-5">
      <div className="relative max-w-4xl w-full mx-auto" onClick={e => e.stopPropagation()} data-id="element-6">
        <div className="bg-white rounded-xl overflow-hidden shadow-2xl" data-id="element-7">
          <div className="flex justify-between items-center p-4 border-b border-slate-200" data-id="element-8">
            <h3 className="font-semibold text-slate-900" data-id="element-9">{altText}</h3>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100 transition-colors" data-id="element-10">
              <X className="w-5 h-5 text-slate-700" data-id="element-11" />
            </button>
          </div>
          <div className="overflow-auto max-h-[80vh]" data-id="element-12">
            <img src={imageUrl} alt={altText} className="w-full h-auto object-contain" data-id="element-13" />
          </div>
        </div>
      </div>
    </div>;
};
const Dashboard = () => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState({
    url: '',
    alt: ''
  });
  // Open modal with specific image
  const openModal = (url, alt) => {
    setModalImage({
      url,
      alt
    });
    setModalOpen(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };
  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    // Restore body scrolling
    document.body.style.overflow = 'auto';
  };
  // Journey data
  const journeyData = [{
    note: 1,
    status: 'Getting started',
    value: 0,
    phase: 'Setup'
  }, {
    note: 7,
    status: 'Research done',
    value: 15,
    phase: 'Setup'
  }, {
    note: 17,
    status: 'Core feature working',
    value: 40,
    phase: 'Building'
  }, {
    note: 35,
    status: 'Working locally!',
    value: 70,
    phase: 'Building'
  }, {
    note: 43,
    status: 'Deployed to prod',
    value: 85,
    phase: 'Building'
  }, {
    note: 44,
    status: 'Wait, it broke',
    value: 55,
    phase: 'Debug'
  }, {
    note: 50,
    status: 'Still debugging...',
    value: 50,
    phase: 'Debug'
  }, {
    note: 61,
    status: 'Fixed!',
    value: 85,
    phase: 'Fixed'
  }, {
    note: 72,
    status: 'Polish time',
    value: 95,
    phase: 'Polish'
  }, {
    note: 88,
    status: 'Done!',
    value: 100,
    phase: 'Polish'
  }];
  const errorTypesData = [{
    name: 'Auth & Keys',
    value: 6
  }, {
    name: 'Prod Environment',
    value: 3
  }, {
    name: 'Data Streams',
    value: 3
  }, {
    name: 'API Calls',
    value: 2
  }, {
    name: 'Platform Limits',
    value: 2
  }, {
    name: 'CORS',
    value: 2
  }, {
    name: 'UI Bugs',
    value: 1
  }, {
    name: 'Logic Errors',
    value: 1
  }];
  return <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 text-slate-900" data-id="element-14">
      {/* Image Modal */}
      <ImageModal isOpen={modalOpen} onClose={closeModal} imageUrl={modalImage.url} altText={modalImage.alt} data-id="element-15" />
      <div className="max-w-6xl mx-auto px-6 py-12" data-id="element-16">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-17">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur border border-purple-200 rounded-full text-purple-700 text-base font-medium mb-6 shadow-sm" data-id="element-18">
            <Sparkles className="w-4 h-4" data-id="element-19" />
            Real talk about coding with Claude
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900" data-id="element-20">
            I spent 20% of my session
            <br data-id="element-21" />
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-22">
              debugging one production bug
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-23">
            Here's what actually happened when I built a feature with Claude
            Code, and what I learned about working with AI
          </p>
        </div>
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16" data-id="element-24">
          <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-slate-200 shadow-sm text-center" data-id="element-25">
            <MessageSquare className="w-7 h-7 text-indigo-600 mx-auto mb-2" data-id="element-26" />
            <div className="text-2xl font-bold text-slate-900 mb-1" data-id="element-27">88</div>
            <div className="text-sm text-slate-600" data-id="element-28">messages total</div>
          </div>
          <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-slate-200 shadow-sm text-center" data-id="element-29">
            <GitCommit className="w-7 h-7 text-purple-600 mx-auto mb-2" data-id="element-30" />
            <div className="text-2xl font-bold text-slate-900 mb-1" data-id="element-31">5</div>
            <div className="text-sm text-slate-600" data-id="element-32">commits</div>
          </div>
          <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-slate-200 shadow-sm text-center" data-id="element-33">
            <CheckCircle className="w-7 h-7 text-emerald-600 mx-auto mb-2" data-id="element-34" />
            <div className="text-2xl font-bold text-slate-900 mb-1" data-id="element-35">20</div>
            <div className="text-sm text-slate-600" data-id="element-36">bugs fixed</div>
          </div>
          <div className="bg-white/80 backdrop-blur rounded-xl p-5 border border-slate-200 shadow-sm text-center" data-id="element-37">
            <RefreshCw className="w-7 h-7 text-pink-600 mx-auto mb-2" data-id="element-38" />
            <div className="text-2xl font-bold text-slate-900 mb-1" data-id="element-39">7</div>
            <div className="text-sm text-slate-600" data-id="element-40">context resets</div>
          </div>
        </div>
        {/* Concept vs Implementation */}
        <div className="bg-white/80 backdrop-blur rounded-2xl p-8 md:p-10 border border-slate-200 shadow-lg mb-16" data-id="element-41">
          <h2 className="text-2xl font-bold mb-4 text-slate-900" data-id="element-42">
            From concept to reality
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-id="element-43">
            Here's what I was trying to build: a feature to let users generate
            styled team photos using AI
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-44">
            <div className="flex flex-col" data-id="element-45">
              <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 mb-4" data-id="element-46">
                <h3 className="text-lg font-semibold text-indigo-900 mb-2 flex items-center gap-2" data-id="element-47">
                  <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm" data-id="element-48">
                    1
                  </span>
                  Initial wireframe
                </h3>
                <div className="aspect-[4/5] rounded-lg overflow-hidden border border-indigo-200 bg-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => openModal('https://uploadthingy.s3.us-west-1.amazonaws.com/hjrGuMssoFr16NZwjnYeSD/14204.png', 'Initial wireframe')} data-id="element-49">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hjrGuMssoFr16NZwjnYeSD/14204.png" alt="Wireframe showing team photo generator concept" className="w-full h-full object-contain" data-id="element-50" />
                </div>
                <div className="text-sm text-indigo-500 text-center mt-2" data-id="element-51">
                  Click to enlarge
                </div>
              </div>
              <div className="text-base text-slate-600 px-4" data-id="element-52">
                <p data-id="element-53">
                  A simple wireframe sketching out the UI for uploading photos,
                  selecting team members, and choosing style options for the
                  AI-generated team image.
                </p>
              </div>
            </div>
            <div className="flex flex-col" data-id="element-54">
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200 mb-4" data-id="element-55">
                <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center gap-2" data-id="element-56">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm" data-id="element-57">
                    2
                  </span>
                  Final implementation
                </h3>
                <div className="aspect-[4/5] rounded-lg overflow-hidden border border-purple-200 bg-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => openModal('https://uploadthingy.s3.us-west-1.amazonaws.com/9g5GyuJhovA9sp67Egb6ii/11918.png', 'Final implementation - Hint Services team page with octopus mascot')} data-id="element-58">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9g5GyuJhovA9sp67Egb6ii/11918.png" alt="Final implementation of Hint Services team page with octopus mascot" className="w-full h-full object-contain" data-id="element-59" />
                </div>
                <div className="text-sm text-purple-500 text-center mt-2" data-id="element-60">
                  Click to enlarge
                </div>
              </div>
              <div className="text-base text-slate-600 px-4" data-id="element-61">
                <p data-id="element-62">
                  The actual feature implementation with Gemini API integration,
                  image processing, and a polished UI that works in production.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-5 border border-indigo-200" data-id="element-63">
            <p className="text-slate-700 text-base italic" data-id="element-64">
              "I wanted to build a tool that would let users upload photos of
              their team members and generate a stylized group photo using AI.
              The feature needed to handle image uploads, process them with the
              Gemini API, and display the results."
            </p>
          </div>
        </div>
        {/* The Main Story */}
        <div className="bg-white/80 backdrop-blur rounded-2xl p-8 md:p-10 border border-slate-200 shadow-lg mb-16" data-id="element-65">
          <div className="mb-8" data-id="element-66">
            <h2 className="text-2xl font-bold mb-4 text-slate-900" data-id="element-67">
              Here's how my time was actually spent
            </h2>
            <p className="text-lg text-slate-600" data-id="element-68">
              Spoiler: Most of it went pretty smoothly. But then production
              happened.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-69">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200" data-id="element-70">
              <div className="flex items-start justify-between mb-5" data-id="element-71">
                <div data-id="element-72">
                  <div className="text-4xl font-bold text-emerald-700 mb-1" data-id="element-73">
                    70
                  </div>
                  <div className="text-base text-slate-700" data-id="element-74">messages</div>
                </div>
                <Heart className="w-10 h-10 text-emerald-500" data-id="element-75" />
              </div>
              <div className="text-slate-900 font-semibold mb-4 text-lg" data-id="element-76">
                Building cool stuff
              </div>
              <ul className="space-y-3 text-slate-700 text-base" data-id="element-77">
                <li className="flex items-start gap-2" data-id="element-78">
                  <span className="text-emerald-600 mt-0.5" data-id="element-79">✓</span>
                  <span data-id="element-80">Researching the Gemini API</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-81">
                  <span className="text-emerald-600 mt-0.5" data-id="element-82">✓</span>
                  <span data-id="element-83">Building the image generation feature</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-84">
                  <span className="text-emerald-600 mt-0.5" data-id="element-85">✓</span>
                  <span data-id="element-86">Creating a nice UI</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-87">
                  <span className="text-emerald-600 mt-0.5" data-id="element-88">✓</span>
                  <span data-id="element-89">Adding tests and polish</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200" data-id="element-90">
              <div className="flex items-start justify-between mb-5" data-id="element-91">
                <div data-id="element-92">
                  <div className="text-4xl font-bold text-orange-700 mb-1" data-id="element-93">
                    18
                  </div>
                  <div className="text-base text-slate-700" data-id="element-94">messages</div>
                </div>
                <Coffee className="w-10 h-10 text-orange-500" data-id="element-95" />
              </div>
              <div className="text-slate-900 font-semibold mb-4 text-lg" data-id="element-96">
                Stuck on one bug
              </div>
              <div className="space-y-4 text-slate-700 text-base" data-id="element-97">
                <p className="leading-relaxed" data-id="element-98">
                  Everything worked perfectly locally. Then I deployed to
                  production and hit a wall: Trigger.dev couldn't upload images
                  to Google Cloud Storage.
                </p>
                <div className="pt-4 border-t border-orange-200" data-id="element-99">
                  <div className="font-medium text-orange-900 mb-2" data-id="element-100">
                    The cost:
                  </div>
                  <p data-id="element-101">Could've built 2-3 more features with that time</p>
                </div>
              </div>
            </div>
          </div>
          {/* Visual bar */}
          <div className="relative mb-6" data-id="element-102">
            <div className="h-20 rounded-xl overflow-hidden flex shadow-md" data-id="element-103">
              <div className="bg-gradient-to-r from-emerald-400 to-teal-400 flex items-center justify-center font-semibold text-white text-base" style={{
              width: '79.5%'
            }} data-id="element-104">
                79.5% productive flow
              </div>
              <div className="bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center font-semibold text-white text-base" style={{
              width: '20.5%'
            }} data-id="element-105">
                20.5% one bug
              </div>
            </div>
          </div>
          <p className="text-center text-base text-slate-600" data-id="element-106">
            This is pretty typical honestly—prod issues can eat up way more time
            than expected
          </p>
        </div>
        {/* The Journey */}
        <div className="bg-white/80 backdrop-blur rounded-2xl p-8 md:p-10 border border-slate-200 shadow-lg mb-16" data-id="element-107">
          <h2 className="text-2xl font-bold mb-4 text-slate-900" data-id="element-108">
            The actual journey
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-id="element-109">
            You can see exactly where I hit the wall (spoiler: around message
            44)
          </p>
          <ResponsiveContainer width="100%" height={320} data-id="element-110">
            <AreaChart data={journeyData} data-id="element-111">
              <defs data-id="element-112">
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-113">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} data-id="element-114" />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0.05} data-id="element-115" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-116" />
              <XAxis dataKey="note" stroke="#64748b" label={{
              value: 'Message number',
              position: 'insideBottom',
              offset: -5,
              fill: '#64748b'
            }} style={{
              fontSize: '14px'
            }} data-id="element-117" />
              <YAxis stroke="#64748b" label={{
              value: 'Progress',
              angle: -90,
              position: 'insideLeft',
              fill: '#64748b'
            }} style={{
              fontSize: '14px'
            }} data-id="element-118" />
              <Tooltip contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '14px'
            }} content={({
              active,
              payload
            }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg" data-id="element-120">
                        <div className="font-semibold text-slate-900 mb-1" data-id="element-121">
                          {data.status}
                        </div>
                        <div className="text-base text-slate-600" data-id="element-122">
                          Message {data.note}
                        </div>
                      </div>;
              }
              return null;
            }} data-id="element-119" />
              <Area type="monotone" dataKey="value" stroke="#a855f7" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" data-id="element-123" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="mt-8 text-center" data-id="element-124">
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-orange-50 border border-orange-200 rounded-lg" data-id="element-125">
              <span className="text-base text-slate-700" data-id="element-126">
                That dip? Yeah, that's where I was debugging for 18 messages
                straight 😅
              </span>
            </div>
          </div>
        </div>
        {/* What went wrong */}
        <div className="bg-white/80 backdrop-blur rounded-xl p-8 md:p-10 border border-slate-200 shadow-lg mb-16" data-id="element-127">
          <h2 className="text-2xl font-bold mb-4 text-slate-900" data-id="element-128">
            What kinds of issues came up
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-id="element-129">
            Out of 20 bugs I fixed, here's what they looked like
          </p>
          <ResponsiveContainer width="100%" height={320} data-id="element-130">
            <BarChart data={errorTypesData} data-id="element-131">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-132" />
              <XAxis dataKey="name" stroke="#64748b" angle={-45} textAnchor="end" height={100} style={{
              fontSize: '14px'
            }} data-id="element-133" />
              <YAxis stroke="#64748b" style={{
              fontSize: '14px'
            }} data-id="element-134" />
              <Tooltip contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '14px'
            }} data-id="element-135" />
              <Bar dataKey="value" fill="#a855f7" radius={[8, 8, 0, 0]} data-id="element-136" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-8 text-center text-base text-slate-600" data-id="element-137">
            Most issues were auth/config stuff—not actually code logic problems
          </div>
        </div>
        {/* The ah-ha moment */}
        <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-8 md:p-10 border border-purple-300 mb-16" data-id="element-138">
          <div className="flex items-start gap-6" data-id="element-139">
            <div className="hidden md:block" data-id="element-140">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0" data-id="element-141">
                <Sparkles className="w-7 h-7 text-white" data-id="element-142" />
              </div>
            </div>
            <div className="flex-1" data-id="element-143">
              <h2 className="text-2xl font-bold mb-5 text-slate-900" data-id="element-144">
                The breakthrough
              </h2>
              <p className="text-xl text-slate-800 mb-5 leading-relaxed" data-id="element-145">
                After 18 messages going in circles with Claude trying different
                fixes, I was exhausted. So I stopped for the night, took a
                shower, and went to bed.
              </p>
              <p className="text-xl text-slate-800 mb-8 leading-relaxed" data-id="element-146">
                The next morning, I did some quick research and found out I
                could use the S3 SDK with Google Cloud Storage. Tried it, and
                boom—it worked immediately.
              </p>
              <div className="bg-white/60 rounded-xl p-6 border border-purple-200" data-id="element-147">
                <div className="font-semibold text-purple-900 mb-3 text-lg" data-id="element-148">
                  What I learned:
                </div>
                <p className="text-slate-800 text-base" data-id="element-149">
                  Sometimes stepping away is more productive than grinding
                  through. AI is amazing at execution, but when you're stuck,
                  bringing in your own research and fresh perspective can break
                  through faster.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Key Takeaways */}
        <div className="bg-gradient-to-br from-slate-900 to-purple-900 rounded-2xl p-8 md:p-10 text-white" data-id="element-150">
          <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-151">
            Things worth remembering
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-152">
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur border border-white/20" data-id="element-153">
              <div className="text-cyan-300 font-semibold mb-3 flex items-center gap-2 text-lg" data-id="element-154">
                <span data-id="element-155">💭</span> Expect the unexpected
              </div>
              <p className="text-slate-100 text-base leading-relaxed" data-id="element-156">
                Add buffer time for weird prod issues. They don't show up
                locally and can take way longer than you think.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur border border-white/20" data-id="element-157">
              <div className="text-purple-300 font-semibold mb-3 flex items-center gap-2 text-lg" data-id="element-158">
                <span data-id="element-159">🔄</span> AI can loop
              </div>
              <p className="text-slate-100 text-base leading-relaxed" data-id="element-160">
                When Claude gets stuck trying similar solutions, that's your cue
                to step in with fresh research or a different angle.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur border border-white/20" data-id="element-161">
              <div className="text-emerald-300 font-semibold mb-3 flex items-center gap-2 text-lg" data-id="element-162">
                <span data-id="element-163">✨</span> Breaks are productive
              </div>
              <p className="text-slate-100 text-base leading-relaxed" data-id="element-164">
                Seriously—shower thoughts solve problems. Don't feel bad about
                stepping away when you're stuck.
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur border border-white/20" data-id="element-165">
              <div className="text-pink-300 font-semibold mb-3 flex items-center gap-2 text-lg" data-id="element-166">
                <span data-id="element-167">🎯</span> It still works
              </div>
              <p className="text-slate-100 text-base leading-relaxed" data-id="element-168">
                Even with the debugging detour, I shipped a complete feature.
                That's pretty wild for one session.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Dashboard;